import { Visit } from '@chiroup/core/types/PatientVisit.type';
import { createContext, ReactNode, use, useContext, useMemo } from 'react';
import { MeContext } from '../../../../../contexts/me.context';
import {
  LOCAL_EDIT_ECHO_WINDOW_MS,
  useCollaborativeEncounter,
  type AwarenessUser,
  type CollaborativeEncounterValue,
  type EncounterDeletionMetadata,
} from '../hooks/useCollaborativeEncounter';
import { useSoapAutoRegeneration } from '../hooks/useSoapAutoRegeneration';

export { LOCAL_EDIT_ECHO_WINDOW_MS };
export type { AwarenessUser, EncounterDeletionMetadata };

type CollaborativeEncounterContextValue = CollaborativeEncounterValue & {
  mostRecentVisit: boolean;
};

const CollaborativeEncounterContext = createContext<CollaborativeEncounterContextValue | null>(
  null
);

interface CollaborativeEncounterProviderProps {
  children: ReactNode;
  encounterId: string;
  enabled?: boolean;
  initialVisit?: Visit | null;
  mostRecentVisit?: boolean;
}

/**
 * Wrapper component that conditionally enables SOAP auto-regeneration
 * when collaborative editing is enabled
 */
function SoapAutoRegenerationWrapper({ enabled }: { enabled: boolean }) {
  // Always call the hook (React rules), but it will check enabled internally
  useSoapAutoRegeneration(enabled);

  return null;
}

/**
 * Internal component that actually establishes the collaborative connection.
 * This is separated so the hook is only called when enabled=true.
 */
function CollaborativeEncounterProviderInner({
  children,
  encounterId,
  initialVisit,
  mostRecentVisit = false,
}: {
  children: ReactNode;
  encounterId: string;
  initialVisit?: Visit | null;
  mostRecentVisit?: boolean;
}) {
  const { me } = use(MeContext);
  const websocketUrl = import.meta.env.VITE_SYNC_SERVER_URL || 'ws://localhost:1234';

  const collaborativeState = useCollaborativeEncounter({
    encounterId,
    clinicId: me?.selectedClinic?.ID || 0,
    websocketUrl,
    userId: me?.ID,
    initialVisit: initialVisit || undefined,
  });

  const contextValue = useMemo(
    () => ({ ...collaborativeState, mostRecentVisit }),
    [collaborativeState, mostRecentVisit]
  );

  return (
    <CollaborativeEncounterContext.Provider value={contextValue}>
      <SoapAutoRegenerationWrapper enabled={true} />
      {children}
    </CollaborativeEncounterContext.Provider>
  );
}

export function CollaborativeEncounterProvider({
  children,
  encounterId,
  enabled = true,
  initialVisit = null,
  mostRecentVisit = false,
}: CollaborativeEncounterProviderProps) {
  // When disabled, just render children without any collaborative features
  // This avoids calling useCollaborativeEncounter hook which would attempt WebSocket connections
  if (!enabled) {
    return children;
  }

  return (
    <CollaborativeEncounterProviderInner
      encounterId={encounterId}
      initialVisit={initialVisit}
      mostRecentVisit={mostRecentVisit}
    >
      {children}
    </CollaborativeEncounterProviderInner>
  );
}

export function useCollaborativeEncounterContext(): CollaborativeEncounterContextValue {
  const context = useContext(CollaborativeEncounterContext);

  if (!context) {
    throw new Error(
      'useCollaborativeEncounterContext must be used within CollaborativeEncounterProvider'
    );
  }

  return context;
}

export function useOptionalCollaborativeEncounter(): CollaborativeEncounterContextValue | null {
  return useContext(CollaborativeEncounterContext);
}
